import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  styled,
  useTheme,
} from '@mui/material';
import type {
  InputLabelProps,
  FormControlProps,
  SelectProps,
  FormHelperTextProps,
  SelectChangeEvent,
} from '@mui/material';
import {
  getCountries as getCountriesLib,
  getCountryCallingCode,
  type CountryCode as LibCountryCode,
} from 'libphonenumber-js';
import { CommonLabelSeparateProps, Locales } from './types';
import { colors } from 'styles/theme/colors';
import { Flag } from './Flag';
import { Text } from 'components/text';
import { InputLabel } from './InputLabel';
import { getInputStyles } from './utils/getInputStyles';
import { getCountryDisplayName } from './utils';
import { useMemo, useState, useRef, useEffect } from 'react';
export const additionalCountryCodeMap: Record<string, CountryCode> = {
  HM: 'AU',
  AQ: 'NF',
  UM: 'US',
  BV: 'NO',
  PN: 'NZ',
  TF: 'YT',
};

const additionalCountry: Country[] = [
  {
    country: 'HM',
    callingCode: '61',
    title: 'Heard Island and McDonald Islands',
  },
  {
    country: 'AQ',
    callingCode: '672',
    title: 'Antarctica',
  },
  {
    country: 'UM',
    callingCode: '1',
    title: 'United States Minor Outlying Islands',
  },
  {
    country: 'BV',
    callingCode: '47',
    title: 'Bouvet Island',
  },
  {
    country: 'PN',
    callingCode: '64',
    title: 'Pitcairn Islands',
  },
  {
    country: 'TF',
    callingCode: '262',
    title: 'French Southern Territories',
  },
];

export type CountryCode = LibCountryCode | 'HM' | 'AQ' | 'UM' | 'BV' | 'PN' | 'TF';
export interface Country {
  country: CountryCode;
  callingCode?: string;
  title?: string;
  enTitle?: string;
}

export const getCountries = (locale: Locales = 'en'): Country[] => {
  const countries = getCountriesLib()?.map(country => {
    return {
      ...(country === 'AC' || country === 'TA' ? { country: `SH-${country}` } : { country }),
      callingCode: getCountryCallingCode(country),
      title: getCountryDisplayName(country, locale),
      enTitle: getCountryDisplayName(country, 'en'),
    };
  });
  return [
    ...(countries ?? []),
    ...(additionalCountry?.map(item => ({
      ...item,
      title: getCountryDisplayName(item?.country, locale),
    })) ?? []),
  ].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')) as Country[];
};

type BaseCountrySelectProps<T> = Omit<
  SelectProps,
  'children' | 'onChange' | 'defaultValue' | 'value'
> &
  CommonLabelSeparateProps & {
    locale?: Locales;
    label?: React.ReactNode;
    helperText?: React.ReactNode;
    labelId?: string;
    id: string;
    formControlProps?: Omit<FormControlProps, 'className' | 'children'>;
    inputLabelProps?: Omit<InputLabelProps, 'id' | 'required' | 'children'>;
    formHelperTextProps?: Omit<FormHelperTextProps, 'children'>;
    showCountryCode?: boolean;
    value?: T;
    defaultValue?: T;
    onChange?: (value: T, event: SelectChangeEvent<CountryCode>, child: React.ReactNode) => void;
  };

export type CountrySelectCountry = BaseCountrySelectProps<Country> & {
  valueType: 'countryObj';
};

export type CountrySelectCountryCode = BaseCountrySelectProps<CountryCode> & {
  valueType: 'countryCode';
};

export type CountrySelectProps = CountrySelectCountry | CountrySelectCountryCode;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 'auto',
      marginTop: '8px',
    },
    sx: {
      maxHeight: 200,
      overflowY: 'auto',
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: colors.mintGreen[600],
        borderRadius: '10px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        backgroundColor: colors.mintGreen[900],
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: colors.mintGreen[300],
        borderRadius: '10px',
      },
    },
  },
};

const CountrySelectBase = ({
  className,
  label,
  locale,
  labelId = 'country-select-id',
  required,
  formControlProps,
  inputLabelProps,
  formHelperTextProps,
  showCountryCode,
  onChange,
  defaultValue: defaultValueProps,
  onBlur: onBlurProps,
  sx,
  value: valueProps,
  valueType = 'countryCode',
  placeholder,
  ...rest
}: CountrySelectProps) => {
  const theme = useTheme();
  const countries = getCountries(locale);

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const getMatchingCountries = (term: string) => {
    if (!term) return [];
    return countries.filter(
      country =>
        country.title?.toLowerCase().startsWith(term.toLowerCase()) ||
        country.callingCode?.toString().startsWith(term) ||
        country.enTitle?.toLowerCase().startsWith(term.toLowerCase())
    );
  };

  const setupSearchTimeout = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm('');
    }, 1000);
  };

  const scrollToFirstMatch = (term: string) => {
    if (term && isOpen) {
      const matchingCountries = getMatchingCountries(term);
      if (matchingCountries.length > 0) {
        const firstMatch = matchingCountries[0];
        const menuItems = menuRef.current?.querySelectorAll('[role="option"]');
        if (menuItems) {
          const targetItem = Array.from(menuItems).find(
            item => item.getAttribute('data-value') === firstMatch.country
          ) as HTMLElement;
          if (targetItem) {
            targetItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            targetItem.focus();
          }
        }
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isOpen && event.key.length === 1) {
      if (/^[a-zA-Z0-9]$/.test(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        const newSearchTerm = searchTerm + event.key;
        setSearchTerm(newSearchTerm);
        setupSearchTimeout();
        scrollToFirstMatch(newSearchTerm);
      }
    } else if (event.key === 'Backspace' && searchTerm) {
      event.preventDefault();
      event.stopPropagation();
      const newSearchTerm = searchTerm.slice(0, -1);
      setSearchTerm(newSearchTerm);
      if (newSearchTerm) {
        setupSearchTimeout();
        scrollToFirstMatch(newSearchTerm);
      }
    }
  };

  const onBlur: SelectProps['onBlur'] = e => {
    onBlurProps?.(e);
  };
  const helperText =
    rest?.helperText && rest?.error ? (
      <Box p={0} m={0} display="flex" alignItems="center" gap="4px">
        {rest.errorIcon} {rest?.helperText}
      </Box>
    ) : (
      rest?.helperText
    );
  const value = useMemo(() => {
    if (!valueProps) return '';
    if (valueType === 'countryObj') {
      if (typeof valueProps === 'object' && 'country' in valueProps) {
        return valueProps.country;
      }
      return '';
    }
    return valueProps;
  }, [valueProps, valueType]) as CountryCode;

  const defaultValue = useMemo(() => {
    if (!defaultValueProps) return '';
    if (valueType === 'countryObj') {
      if (typeof defaultValueProps === 'object' && 'country' in defaultValueProps) {
        return defaultValueProps.country;
      }
      return '';
    }
    return defaultValueProps;
  }, [defaultValueProps, valueType]) as CountryCode;

  return (
    <FormControl fullWidth {...formControlProps} className={className}>
      {label && (
        <InputLabel
          {...inputLabelProps}
          isUnderline={rest?.isUnderline}
          labelVariant={rest?.labelVariant}
          labelColor={rest?.labelColor}
          labelWeight={rest?.labelWeight}
          required={required}
          error={rest?.error}
          disabled={rest.disabled}
          shrink
          id={labelId}
        >
          {label}
        </InputLabel>
      )}
      <Select
        {...rest}
        sx={sx}
        value={value}
        defaultValue={defaultValue}
        MenuProps={{
          ...MenuProps,
          ref: menuRef,
          onKeyDown: handleKeyDown,
        }}
        required={required}
        labelId={labelId}
        displayEmpty
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false);
          setSearchTerm('');
        }}
        onChange={(event, child) => {
          const countryCode = event?.target.value as CountryCode;
          if (valueType === 'countryObj') {
            const newValue = countries?.find(item => item?.country === countryCode) as Country;
            (onChange as CountrySelectCountry['onChange'])?.(newValue, event, child);
          } else {
            (onChange as CountrySelectCountryCode['onChange'])?.(countryCode, event, child);
          }
        }}
        onBlur={onBlur}
        SelectDisplayProps={{ style: { display: 'flex', gap: '8px', alignItems: 'center' } }}
        {...(value
          ? { renderValue: rest?.renderValue }
          : {
              renderValue: () => (
                <Text
                  variant="bodyMd"
                  className="placeholder_country"
                  color={theme.palette.text.placeholder}
                >
                  {placeholder}
                </Text>
              ),
            })}
      >
        {countries.map(({ country, title, callingCode }) => {
          const isHighlighted =
            searchTerm &&
            (title?.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
              callingCode?.toString().startsWith(searchTerm));

          return (
            <MenuItem
              key={country}
              value={country}
              data-value={country}
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                height: '44px',
                backgroundColor: isHighlighted ? theme.palette.action.hover : 'transparent',
              }}
            >
              <Flag nationality={country} /> {showCountryCode && <Text>+{callingCode}</Text>}{' '}
              <Text variant="bodySm" color={theme.palette.text.secondary}>
                {title}
              </Text>
            </MenuItem>
          );
        })}
      </Select>
      {helperText && (
        <FormHelperText {...formHelperTextProps} error={rest?.error} disabled={rest.disabled}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const CountrySelect = styled(
  ({
    labelSeparate,
    errorIconProps,
    errorIcon,
    helperText: hint,
    labelVariant,
    labelWeight,
    isUnderline,
    ...styledProps
  }: CountrySelectProps) => {
    return <CountrySelectBase errorIcon={errorIcon} helperText={hint} {...styledProps} />;
  }
)(({ theme, labelSeparate, labelVariant, isUnderline, labelWeight, labelColor, ...rest }) => {
  return {
    ...getInputStyles({
      theme,
      labelSeparate,
      variant: rest?.variant,
      isUnderline,
      labelColor,
      labelVariant,
      labelWeight,
    }),
  };
});

export const CountrySelectItem = ({
  country,
  callingCode,
  title,
  showCountryCode,
  height = '44px',
}: Partial<Country> & { showCountryCode?: boolean; height?: string }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', height }}>
      <Flag nationality={country} /> {showCountryCode && <Text>+{callingCode}</Text>}{' '}
      <Text color={theme.palette.text.secondary}>{title}</Text>
    </Box>
  );
};
